import { DEFAULT_LANGUAGE } from '../config/app';
import esLocale from './locales/es';

type Dict = Record<string, string>;

const cache: Partial<Record<'en' | 'es', Dict>> = {
  // Default product language — first paint / Splash without waiting on en chunk.
  es: esLocale,
};

const loaders: Record<'en' | 'es', () => Promise<{ default: Dict }>> = {
  en: () => import('./locales/en'),
  es: () => import('./locales/es'),
};

async function loadLocale(lang: 'en' | 'es'): Promise<Dict> {
  if (cache[lang]) return cache[lang]!;
  const mod = await loaders[lang]();
  cache[lang] = mod.default;
  return mod.default;
}

/**
 * Ensure a locale is in memory (lazy chunk). Safe to call on language switch.
 */
export async function ensureLocale(lang: string): Promise<Dict> {
  const key = lang === 'en' ? 'en' : 'es';
  return loadLocale(key);
}

/** Sync snapshot — may fall back to default until `ensureLocale` resolves. */
export const translations = {
  get en() {
    return cache.en ?? cache.es ?? esLocale;
  },
  get es() {
    return cache.es ?? esLocale;
  },
};

/**
 * @param {'en'|'es'|string} lang
 * @returns {Record<string, string>}
 */
export function useTranslation(lang: string = DEFAULT_LANGUAGE): Dict {
  const key = lang === 'en' ? 'en' : 'es';
  if (cache[key]) return cache[key]!;
  // Kick off lazy load; return default language until ready.
  void loadLocale(key);
  return cache[DEFAULT_LANGUAGE as 'es'] ?? esLocale;
}

// Prefetch non-default locale after idle so language toggle is snappy.
if (!import.meta.env.VITEST && typeof window !== 'undefined') {
  const prefetch = () => {
    void loadLocale('en');
  };
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(prefetch, { timeout: 4000 });
  } else {
    globalThis.setTimeout(prefetch, 2500);
  }
}
